import { Component, Inject, Input, OnInit, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Billing } from "src/app/common/interfaces/home/billing.interface";
import { StorageService } from "src/app/common/services/storage.service";
import { Router } from "@angular/router";
import { distinctUntilChanged, firstValueFrom, Subscription } from "rxjs";
import { Tab } from "src/app/common/interfaces/tab";
import { TabChange } from "src/app/common/interfaces/tab-change";
import { TabsComponent } from "../../../common/components/core/tabs/tabs.component";
import { AppConstants } from "src/app/common/constants/app-constants";
import { BillingTiltDetails, TherapistAgendaBilling } from "src/app/common/interfaces/home/billingTile-details.interface";
import { LandingClientService } from "src/app/common/services/landing-page.client";
import { NotesStatus } from "src/app/common/enums/Notes-status.enum";

@Component({
  selector: "app-billing",
  standalone: true,
  imports: [CommonModule, TabsComponent],
  templateUrl: "./billing.component.html",
  styleUrls: ["./billing.component.scss"],
})
export class BillingComponent implements OnInit {
  private facilitySubscription: Subscription | null = null;
  private readonly tabMetricList: string[] = [AppConstants.pastFourtyEightHours, AppConstants.lastWeek, AppConstants.lastFourteenDays]

  billingTiltDetails!: BillingTiltDetails;
  billings: Billing[] = [];
  billingDetails!: TherapistAgendaBilling[];
  facilityId: string = "";
  tabIndex = 0;
  tabs: Tab[] = this.tabMetricList.map((name) => ({ name }));
  NotesStatusOptions = {
    Complete: NotesStatus.Complete,
    Partially: NotesStatus.PartiallyComplete,
    Incomplete: NotesStatus.Incomplete
  };
  appConstant = AppConstants;
  

  constructor(
    private landingPageClient: LandingClientService,
    private storageService: StorageService,
    private router: Router
  ) {}

  async ngOnInit() {
    this.facilitySubscription = this.storageService.isSelectedFacility
      .pipe(distinctUntilChanged())
      .subscribe(async(facilityId) => {
        this.facilityId = facilityId;
        await this.getBillingTileDetails();
        this.initiateBillingDetails();
        this.prepareBillingData();
      });
  }

  initiateBillingDetails(){
    const selectedTabName = this.tabs[this.tabIndex].name;
    switch (selectedTabName) {
      case AppConstants.pastFourtyEightHours:
        this.billingDetails = this.billingTiltDetails.pastFourtyEightHours;
        break;

      case AppConstants.lastWeek:
        this.billingDetails = this.billingTiltDetails.lastWeek;
        break;
      
      case AppConstants.lastFourteenDays:
        this.billingDetails = this.billingTiltDetails.pastTwoWeeks;
        break;
    
      default:
        this.billingDetails = this.billingTiltDetails.pastFourtyEightHours;
        break;
    }
  }

  async getBillingTileDetails(){
    this.billingTiltDetails = await firstValueFrom(this.landingPageClient.getBillingTileDetails(this.facilityId))
  }

  ngOnDestroy() {
    this.facilitySubscription?.unsubscribe();
  }

  tabChange(tab: TabChange) {
    this.tabIndex = tab.to;
    this.initiateBillingDetails()
    this.prepareBillingData();
  }

  prepareBillingData() {
    if (this.billingDetails) {
      this.billings = [];
      this.billingDetails?.forEach((agenda) => {
        this.billings.push({
          patientId: agenda.patientId,
          name: agenda.patientName,
          noteStatus: agenda.noteStatus,
          billedMins: (agenda.billedMinutes ?? 0).toString(),
          agendaMins: (agenda.agendaMins ?? 0).toString(),
          isAgenda: (agenda.agendaMins ?? 0) > 0,
          isBilled: (agenda.billedMinutes ?? 0) > 0,
          isBilledError:
            ((agenda.billedMinutes ?? 0) > 0 || (agenda.agendaMins ?? 0) > 0) &&
            (agenda.billedMinutes ?? 0) !== (agenda.agendaMins ?? 0),
          discipline: agenda.discipline,
          mode: agenda.mode,
          date: this.formatDate(agenda.therapyAgendaDate)
        });
      });
    }
  }

  formatDate(dateString: string): string {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  }

  routeToBillingPage(patientId: string) {
    this.router.navigateByUrl(
      `/patient/${this.facilityId}/profile/${patientId}/billing`
    );
  }
}
